/**
 * Single source of truth for metric names and definitions (spec §7).
 * Components must render labels from here — never hand-written strings —
 * so the two SPY measures can never blur together again.
 */
export type MetricKey =
  | 'excess_vs_spy_since_entry'
  | 'excess_vs_spy_today'
  | 'excess_vs_spy_window'
  | 'trend_excess_126d'
  | 'day_pnl'
  | 'book_return'
  | 'shadow_excess_since_flag'
  | 'tax_reportable'
  | 'tax_economic'
  | 'tax_deferred_net'

export interface MetricDef {
  label: string        // full header label
  short: string        // tight-column header
  definition: string   // one-sentence InfoTip body
}

export const METRICS: Record<MetricKey, MetricDef> = {
  excess_vs_spy_since_entry: {
    label: 'Excess vs SPY · since entry',
    short: 'Excess vs SPY',
    definition:
      "The pick's return since its entry date minus SPY's return over the same dates. Positive = the pick beat simply holding the index.",
  },
  excess_vs_spy_today: {
    label: 'Excess vs SPY · today',
    short: 'Excess today',
    definition:
      "Today's return minus SPY's return today. Positive = beating the market today.",
  },
  excess_vs_spy_window: {
    label: 'Excess vs SPY · window',
    short: 'Excess (window)',
    definition:
      "The book's equity change over the selected window minus SPY's return over the same window. Longer windows net out deposits/withdrawals recorded in the flow ledger; when flows are unavailable the raw change is shown and captioned. Only the dollar change is flow-netted — the percentage is a simple return against starting equity, so a mid-window deposit will overstate the capital-weighted return.",
  },
  trend_excess_126d: {
    label: 'Trend excess · 126d',
    short: 'Trend 126d',
    definition:
      '126-day cumulative excess return vs SPY — pick-ranking momentum strength. A price-momentum measure, not a fundamentals or earnings-surprise signal.',
  },
  day_pnl: {
    label: 'Day P&L',
    short: 'Day P&L',
    definition:
      "Change in book value since yesterday's close, adjusted for deposits/withdrawals — the same baseline Alpaca's own UI uses.",
  },
  book_return: {
    label: 'Book return',
    short: 'Return',
    definition:
      "The book's percentage change in equity over the selected timeframe window. " +
      "For DAY this nets out deposits/withdrawals (flow-adjusted). Longer windows " +
      "net out deposits/withdrawals recorded in the flow ledger; when flows are " +
      "unavailable the raw first-to-last equity change is shown and captioned. " +
      "Only the dollar change is flow-netted — the percentage is a simple return " +
      "against starting equity, so a mid-window deposit will overstate the " +
      "capital-weighted return.",
  },
  shadow_excess_since_flag: {
    label: 'Shadow excess · since flag',
    short: 'Shadow vs SPY',
    definition:
      "The candidate's return since its discovery (flag) date minus SPY's return over the same span — how the engine's eye is performing before capital is committed. Not a P&L figure.",
  },
  tax_reportable: {
    label: 'Reportable (tax)',
    short: 'Reportable',
    definition:
      'What Form 8949 will show: realized gains/losses with wash-sale losses removed.',
  },
  tax_economic: {
    label: 'Realized (economic)',
    short: 'Economic',
    definition:
      'What actually happened to the money: proceeds minus unadjusted basis, before wash-sale deferrals.',
  },
  tax_deferred_net: {
    label: 'Deferred into basis (net)',
    short: 'Deferred (net)',
    definition:
      'Wash-sale losses parked in replacement-lot basis, net of amounts already recovered on later sales.',
  },
}
